import { Button } from '@/components/ui/button';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Package, User, FileText, ShoppingCart } from 'lucide-react';
import { IOrder, OrderStatus } from '@/api/orders';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import TextUserInactive from '@/components/TextUserInactive';
import TextCodeOrder from '@/components/TextCodeOrder';
import OrderStatusRender from './orderStatus';
import { useSendToManagerMarketing } from '@/hooks/useOrder';
import LoadingCircle from '@/components/LoadingCircle';
import { useProfileContext } from '@/hooks/useProfileContext';
import { formatCurrency } from '@/lib/utils';

function DetailOrder({ order }: { order: IOrder }) {
  const { profile } = useProfileContext();
  const [open, setOpen] = useState(false);
  const { mutateAsync: sendToManagerMarketing, isPending } =
    useSendToManagerMarketing();

  // Format date if available
  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return format(new Date(dateString), 'dd/MM/yyyy', { locale: vi });
  };

  const handleSendToManagerMarketing = async () => {
    await sendToManagerMarketing(order.id);
    setOpen(false);
  };

  const total = order.items.reduce((sum, item) => {
    return sum + Number(item.price) * item.quantity;
  }, 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="p-0 bg-transparent border-none shadow-none group"
        >
          <TextCodeOrder
            className="cursor-pointer transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl"
            id={order.id}
          />
        </Button>
      </DialogTrigger>
      <DialogContent className="min-w-[1000px] max-h-[95vh] overflow-y-auto luxury-theme">
        <DialogDescription className="sr-only">
          Chi tiết đơn hàng
        </DialogDescription>
        <DialogHeader className="luxury-header">
          <DialogTitle className="luxury-title">
            Thông tin chi tiết đơn hàng
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-8">
          {/* Thông tin sản phẩm */}
          <Card className="luxury-card luxury-theme">
            <CardHeader className="pb-2 border-b border-luxury-100">
              <CardTitle className="text-lg flex items-center text-luxury-900">
                <Package className="w-5 h-5 mr-3 text-luxury-600" />
                Thông tin sản phẩm - Tổng tiền:{' '}
                <span className="font-bold text-luxury-800">
                  {formatCurrency(total)}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 gap-4">
                {order.items.map((item) => (
                  <div
                    key={item.id}
                    className="p-4 border border-luxury-100 rounded-lg bg-white/50 backdrop-blur-sm shadow-sm hover:shadow-md transition-all duration-300"
                  >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                      <div className="flex flex-col space-y-1">
                        <span className="luxury-label text-xs text-luxury-500 uppercase tracking-wider">
                          Tên sản phẩm
                        </span>
                        <span className="font-medium text-luxury-800 line-clamp-1">
                          {item.productName}
                        </span>
                      </div>

                      <div className="flex flex-col space-y-1">
                        <span className="luxury-label text-xs text-luxury-500 uppercase tracking-wider">
                          Số lượng
                        </span>
                        <span className="font-medium text-luxury-800">
                          {item.quantity}
                        </span>
                      </div>

                      <div className="flex flex-col space-y-1">
                        <span className="luxury-label text-xs text-luxury-500 uppercase tracking-wider">
                          Giá
                        </span>
                        <span className="font-medium text-luxury-800">
                          {formatCurrency(Number(item.price))}
                        </span>
                      </div>

                      <div className="flex flex-col space-y-1">
                        <span className="luxury-label text-xs text-luxury-500 uppercase tracking-wider">
                          Serial Number
                        </span>
                        <span className="font-medium text-luxury-800">
                          {item.serialNumber || '—'}
                        </span>
                      </div>
                    </div>

                    {item.description && (
                      <div className="flex flex-col space-y-1 mt-3 pt-3 border-t border-luxury-100">
                        <span className="luxury-label text-xs text-luxury-500 uppercase tracking-wider">
                          Mô tả
                        </span>
                        <span className="font-medium text-luxury-800 text-sm">
                          {item.description}
                        </span>
                      </div>
                    )}
                  </div>
                ))}

                <div className="grid grid-cols-3 gap-4 p-4 border border-luxury-100 rounded-lg bg-white/50 backdrop-blur-sm shadow-sm">
                  <div className="flex flex-col space-y-1">
                    <span className="luxury-label text-xs text-luxury-500 uppercase tracking-wider">
                      Số tiền phải trả
                    </span>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-luxury-800 text-sm">
                        {formatCurrency(order.amountToBePaid || total)}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col space-y-1">
                    <span className="luxury-label text-xs text-luxury-500 uppercase tracking-wider">
                      Ngày bán
                    </span>
                    <span className="font-medium text-luxury-800 text-sm">
                      {formatDate(order.sellDate)}
                    </span>
                  </div>

                  <div className="flex flex-col space-y-1">
                    <span className="luxury-label text-xs text-luxury-500 uppercase tracking-wider">
                      Ngày tạo
                    </span>
                    <span className="font-medium text-luxury-800 text-sm">
                      {formatDate(order.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Thông tin khách hàng */}
          <Card className="luxury-card luxury-theme">
            <CardHeader className="pb-2 border-b border-luxury-100">
              <CardTitle className="text-lg flex items-center text-luxury-900">
                <User className="w-5 h-5 mr-3 text-luxury-600" />
                Thông tin khách hàng
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Họ và tên</span>
                  <span className="font-medium text-luxury-800">
                    {order.customer.fullName}
                  </span>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Số điện thoại</span>
                  <span className="font-medium text-luxury-800">
                    {order.customer.phoneNumber}
                  </span>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Email</span>
                  <span className="font-medium text-luxury-800">
                    {order.customer.email}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Thông tin nhân viên */}
          <Card className="luxury-card luxury-theme">
            <CardHeader className="pb-2 border-b border-luxury-100">
              <CardTitle className="text-lg flex items-center text-luxury-900">
                <ShoppingCart className="w-5 h-5 mr-3 text-luxury-600" />
                Thông tin nhân viên
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Nhân viên tạo đơn</span>
                  <span className="font-medium text-luxury-800">
                    {order.createdBy.name}
                    <TextUserInactive status={order.createdBy.status} />
                  </span>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Nhân viên phụ trách</span>
                  <span className="font-medium text-luxury-800">
                    {order.employee.name}
                    <TextUserInactive status={order.employee.status} />
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Thông tin hệ thống */}
          <Card className="luxury-card luxury-theme">
            <CardHeader className="pb-2 border-b border-luxury-100">
              <CardTitle className="text-lg flex items-center text-luxury-900">
                <FileText className="w-5 h-5 mr-3 text-luxury-600" />
                Thông tin hệ thống
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Trạng thái</span>
                  <div className="flex items-center gap-2">
                    <OrderStatusRender status={order.status} />
                  </div>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">ID</span>
                  <span className="font-medium text-sm font-mono bg-luxury-50 p-1 rounded border border-luxury-100 text-luxury-800">
                    {order.id}
                  </span>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Cập nhật lần cuối</span>
                  <span className="font-medium text-luxury-800">
                    {formatDate(order.updatedAt)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          {order.status === OrderStatus.NEW &&
            order.employee.id === profile?.id && (
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  className="luxury-button-primary"
                  onClick={handleSendToManagerMarketing}
                  disabled={isPending}
                >
                  {isPending ? <LoadingCircle /> : 'Gửi đơn hàng đến kế toán'}
                </Button>
              </div>
            )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DetailOrder;
